<script lang="ts">
  import { storyblokEditable } from '@storyblok/svelte';
  import { PageSection } from '@athena-lib/Page';
  import type { HeadingSize } from '@athena-lib/Text';
  import { BaseHeading, Text } from '@athena-lib/Text';
  import { pageSectionYSpaceCaster } from '@athena-lib/Page/types';
  import { getLocalizationContext } from '@athena-lib/i18n';
  import { Grid, GridItem } from '@athena-lib/Grid';
  import { pageSectionBgCaster } from '@athena-lib/container-context.svelte';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { castColor } from '@athena-lib/colors';
  import Image from '@athena-lib/Image/Image.svelte';
  import { ArrowBack } from '@athena-lib/Icons';
  import { Button } from '@athena-lib/Button';
  import ArrowForward from '@athena-lib/Icons/ArrowForward.svelte';
  import { parseHeadingLevel } from '@athena-lib/Text/BaseHeading/types';
  import type { ExpandedRecentBlogPostsPageSection } from '$lib/server/expanders/recentBlogPostsExpander';

  let { blok }: { blok: ExpandedRecentBlogPostsPageSection } = $props();

  const locale = getLocalizationContext();
  const format = Intl.DateTimeFormat(locale.language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const formatPostDate = (date: string | undefined) =>
    date ? format.format(new Date(date)) : undefined;

  const perPage = 10;
  let page = $state(0);
  const totalPages = $derived(Math.floor((blok.recentBlogPosts?.length ?? 0) / perPage));
</script>

<PageSection
  id={blok.elementId}
  width="large"
  action={storyblokEditable}
  actionArg={blok}
  ySpace={{
    top: pageSectionYSpaceCaster(blok.topPadding) ?? 'medium',
    bottom: pageSectionYSpaceCaster(blok.bottomPadding) ?? 'medium',
  }}
  backgroundColor={pageSectionBgCaster(blok.backgroundColor) ?? 'light'}
>
  <Stack space="medium">
    {#if blok.headingText}
      <StackItem>
        <BaseHeading
          level={parseHeadingLevel(blok.headingTag) ?? 2}
          size={blok.headingSize as HeadingSize}
          color={castColor(blok.headingColor)}
        >
          {blok.headingText}
        </BaseHeading>
      </StackItem>
    {/if}
    <StackItem>
      <div class="gap-md flex flex-col">
        <Grid columns={{ sm: 1, md: 2 }}>
          {#each blok.recentBlogPosts?.slice(page * perPage, (page + 1) * perPage) ?? [] as post}
            <GridItem class="flex gap-5">
              <div class="content-center">
                {#if post.image}
                  <Image
                    imgClass="aspect-5/7 w-24 min-w-24 object-cover md:aspect-square md:w-40 md:min-w-40"
                    src={post.image.filename}
                    widthOfContainer={0.01}
                    alt={post.image.alt ?? ''}
                  />
                {/if}
              </div>
              <div class="flex grow flex-col justify-center gap-1">
                <div class="flex content-center justify-between">
                  <Text size="medium" bold color="red">
                    <a href={post.category?.url}>{post.category?.name}</a>
                  </Text>
                  <Text size="small" class="block md:hidden">
                    {formatPostDate(post.date)}
                  </Text>
                </div>
                <Text size="xlarge" bold>
                  <a href={post.articleUrl}>{post.title}</a>
                </Text>
                <Text size="medium" class="line-clamp-2">{post.preamble}</Text>
                <Text size="small" class="hidden md:block">{formatPostDate(post.date)}</Text>
              </div>
            </GridItem>
          {/each}
        </Grid>
        {#if totalPages > 1}
          <ul class="flex justify-center gap-4 pt-medium" aria-label="pagination">
            <li>
              <Button
                size="small"
                color="dark"
                class="pl-2 pr-2"
                disabled={page <= 0}
                onClick={() => {
                  page = page - 1;
                }}
              >
                <ArrowBack sizeClass="h-5 w-5" />
              </Button>
            </li>
            <li class="content-center">{page + 1} / {totalPages + 1}</li>
            <li>
              <Button
                size="small"
                color="dark"
                class="pl-2 pr-2"
                disabled={page >= totalPages}
                onClick={() => {
                  page = page + 1;
                }}
              >
                <ArrowForward sizeClass="h-5 w-5" />
              </Button>
            </li>
          </ul>
        {/if}
      </div>
    </StackItem>
  </Stack>
</PageSection>
